import React from 'react';
import { cn } from '../../utils/cn';

// Text types
export type TextVariant =
  | 'body'         // Body text
  | 'heading'      // Heading text
  | 'caption'      // Caption/small text
  | 'label'        // Label text
  | 'quote'        // Blockquote
  | 'code'         // Inline code
  | 'pre'          // Preformatted text
  | 'lead'         // Lead paragraph

export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl' | '9xl';
export type TextWeight = 'thin' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextColor = 'primary' | 'secondary' | 'muted' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'inherit';
export type TextDecoration = 'none' | 'underline' | 'line-through' | 'overline';
export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';
export type TextAs = 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'small' | 'strong' | 'em' | 'mark' | 'del' | 'ins' | 'sub' | 'sup' | 'code' | 'pre' | 'blockquote';

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color' | 'onCopy'> {
  // Content
  children?: React.ReactNode;

  // Typography
  variant?: TextVariant;
  size?: TextSize;
  weight?: TextWeight;
  align?: TextAlign;
  color?: TextColor;
  decoration?: TextDecoration;
  transform?: TextTransform;

  // Element
  as?: TextAs;

  // Spacing
  noMargin?: boolean;
  leading?: 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';
  tracking?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider' | 'widest';

  // Visual
  gradient?: boolean;
  gradientFrom?: string;
  gradientTo?: string;
  truncate?: boolean | number;  // true for single line, number for multi-line
  wrap?: 'normal' | 'nowrap' | 'pre' | 'pre-line' | 'pre-wrap';

  // Interactive
  selectable?: boolean;
  copyable?: boolean;
  onCopy?: (text: string) => void;
}

// Size mappings
const sizeClasses: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
  '7xl': 'text-7xl',
  '8xl': 'text-8xl',
  '9xl': 'text-9xl'
};

// Weight mappings
const weightClasses: Record<TextWeight, string> = {
  thin: 'font-thin',
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
  black: 'font-black'
};

// Align mappings
const alignClasses: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify'
};

// Color mappings (using semantic tokens)
const colorClasses: Record<TextColor, string> = {
  primary: 'text-foreground',
  secondary: 'text-muted-foreground',
  muted: 'text-muted-foreground',
  accent: 'text-primary',
  success: 'text-status-success',
  error: 'text-destructive',
  warning: 'text-status-warning',
  info: 'text-status-info',
  inherit: 'text-inherit'
};

// Decoration mappings
const decorationClasses: Record<TextDecoration, string> = {
  none: 'no-underline',
  underline: 'underline',
  'line-through': 'line-through',
  overline: 'overline'
};

// Transform mappings
const transformClasses: Record<TextTransform, string> = {
  none: 'normal-case',
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize'
};

// Leading mappings
const leadingClasses: Record<NonNullable<TextProps['leading']>, string> = {
  none: 'leading-none',
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose'
};

// Tracking mappings
const trackingClasses: Record<NonNullable<TextProps['tracking']>, string> = {
  tighter: 'tracking-tighter',
  tight: 'tracking-tight',
  normal: 'tracking-normal',
  wide: 'tracking-wide',
  wider: 'tracking-wider',
  widest: 'tracking-widest'
};

// Wrap mappings
const wrapClasses: Record<NonNullable<TextProps['wrap']>, string> = {
  normal: 'whitespace-normal',
  nowrap: 'whitespace-nowrap',
  pre: 'whitespace-pre',
  'pre-line': 'whitespace-pre-line',
  'pre-wrap': 'whitespace-pre-wrap'
};

// Default element for each variant
const variantElements: Record<TextVariant, TextAs> = {
  body: 'p',
  heading: 'h2',
  caption: 'small',
  label: 'label',
  quote: 'blockquote',
  code: 'code',
  pre: 'pre',
  lead: 'p'
};

// Default styles for each variant
const variantDefaults: Record<TextVariant, { size: TextSize; weight: TextWeight; color: TextColor }> = {
  body: { size: 'base', weight: 'normal', color: 'primary' },
  heading: { size: '2xl', weight: 'bold', color: 'primary' },
  caption: { size: 'sm', weight: 'normal', color: 'muted' },
  label: { size: 'sm', weight: 'medium', color: 'primary' },
  quote: { size: 'lg', weight: 'normal', color: 'secondary' },
  code: { size: 'sm', weight: 'normal', color: 'accent' },
  pre: { size: 'sm', weight: 'normal', color: 'primary' },
  lead: { size: 'xl', weight: 'light', color: 'secondary' }
};

// Copy icon
const CopyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

export const Text: React.FC<TextProps> = ({
  className = '',
  children,
  variant = 'body',
  size,
  weight,
  align = 'left',
  color,
  decoration = 'none',
  transform = 'none',
  as,
  noMargin = false,
  leading = 'normal',
  tracking = 'normal',
  gradient = false,
  gradientFrom = 'from-accent-primary',
  gradientTo = 'to-accent-hover',
  truncate = false,
  wrap = 'normal',
  selectable = true,
  copyable = false,
  onCopy,
  ...props
}) => {
  const [copied, setCopied] = React.useState(false);

  // Get defaults based on variant
  const defaults = variantDefaults[variant];
  const finalSize = size || defaults.size;
  const finalWeight = weight || defaults.weight;
  const finalColor = color || defaults.color;
  const Component = as || variantElements[variant];

  // Handle copy functionality
  const handleCopy = async () => {
    const text = typeof children === 'string' ? children : '';

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (onCopy) onCopy(text);

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Build truncate classes
  const truncateClasses = truncate === true
    ? 'truncate'
    : typeof truncate === 'number'
    ? `line-clamp-${truncate}`
    : '';

  // Special classes for specific variants
  const variantSpecificClasses: Record<TextVariant, string> = {
    body: '',
    heading: '',
    caption: 'block',
    label: 'block',
    quote: 'pl-4 border-l-4 border-input italic',
    code: 'px-1 py-0.5 bg-secondary rounded font-mono',
    pre: 'p-4 bg-secondary rounded overflow-x-auto font-mono',
    lead: ''
  };

  const classes = cn(
    // Size
    sizeClasses[finalSize],

    // Weight
    weightClasses[finalWeight],

    // Alignment
    alignClasses[align],

    // Color (unless gradient)
    !gradient && colorClasses[finalColor],

    // Gradient
    gradient && cn(
      'bg-gradient-to-r bg-clip-text text-transparent',
      gradientFrom,
      gradientTo
    ),

    // Decoration
    decorationClasses[decoration],

    // Transform
    transformClasses[transform],

    // Spacing
    leadingClasses[leading],
    trackingClasses[tracking],
    wrapClasses[wrap],

    // Truncate
    truncateClasses,

    // Selectable
    !selectable && 'select-none',

    // Margins (for block elements)
    !noMargin && Component !== 'span' && 'mb-4 last:mb-0',

    // Variant specific
    variantSpecificClasses[variant],

    // Copyable
    copyable && 'group relative',

    // Custom className
    className
  );

  return (
    <Component className={classes} {...props}>
      {children}

      {/* Copy button */}
      {copyable && (
        <button
          onClick={handleCopy}
          className={cn(
            "absolute right-0 top-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
            "bg-secondary hover:bg-hover text-muted-foreground hover:text-primary",
            copied && "text-status-success"
          )}
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <CopyIcon className="w-4 h-4" />
          )}
        </button>
      )}
    </Component>
  );
};

Text.displayName = 'Text';

// Convenience exports for common text types
export const Heading: React.FC<Omit<TextProps, 'variant'> & { level?: 1 | 2 | 3 | 4 | 5 | 6 }> = ({ level = 2, ...props }) => (
  <Text variant="heading" as={`h${level}` as TextAs} {...props} />
);

export const Body: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="body" {...props} />
);

export const Caption: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="caption" {...props} />
);

export const Label: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="label" {...props} />
);

export const Quote: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="quote" {...props} />
);

export const Code: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="code" {...props} />
);

export const Pre: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="pre" {...props} />
);

export const Lead: React.FC<Omit<TextProps, 'variant'>> = (props) => (
  <Text variant="lead" {...props} />
);
